import { memo, useCallback, useState, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { IoClose } from "react-icons/io5";
import { CarouselPageContainer, CarouselContainer, CarouselImage, CarouselLeftButton, CarouselRightButton, CarouselSlide, CarouselViewportWithHover, CarouselWrapper, CarouselMobileBackButton, CarouselMobileShareButton, CarouselMobileSaveButton } from "../viewListingStyling";
import { ViewListingFeaturedImage } from "../skeletonViewListingPage/skeletonViewListingPageStyling";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecoilState } from "recoil";
import { userModelSelector } from "../../../state/selectors/userModel";
import { accessTokenSelector } from "../../../state/selectors/accessToken";
import { signInMenuActivatedSelector } from "../../../state/selectors/navbarToggle";
import { toggleSaved } from "../../../utils/savedListings/toggleSaved";
import { ColorTheme, TextTheme } from "../../../components/globaStyledComponents/globalStyledComponentsStyling";
import styled from "styled-components";

const PhotoCountIndicator = styled.div`
    position: absolute;
    bottom: 36px;
    right: 12px;
    background: rgba(0, 0, 0, 0.6);
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: ${TextTheme.sans_200_semi.fontSize};
    font-family: ${TextTheme.sans_200_semi.fontFamily};
    line-height: ${TextTheme.sans_200_semi.lineHeight};
    z-index: 2;
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    gap: 4px;
    @media (min-width: 768px) {
        display: none;
    }
`;

const CameraIcon = styled.svg`
    width: 12px;
    height: 12px;
    fill: none;
    stroke: white;
`;


interface ViewListingCarouselProps {
    eventOnImageClick: (index: number) => void;
    images: string[];
    listingId: string;
    listingTitle: string;
    propertyDetails: {
        guests: number;
        bedrooms: number;
        beds: number;
        baths: number;
        area: number;
    };
    isSaved: boolean;
    setIsSaved: React.Dispatch<React.SetStateAction<boolean>>;
    toggleShareListing: () => void;
}

const ViewListingCarousel = memo(function ViewListingCarousel({ eventOnImageClick, images, listingId, listingTitle, propertyDetails, isSaved, setIsSaved, toggleShareListing }: ViewListingCarouselProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const [userModel] = useRecoilState(userModelSelector);
    const [accessToken] = useRecoilState(accessTokenSelector);
    const [, setSignInMenuActivated] = useRecoilState(signInMenuActivatedSelector);
    const isSavedLoading = useRef(false);
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [loadedImagesCount, setLoadedImagesCount] = useState(0);
    
    useEffect(() => {
        if (userModel && userModel.savedListings.includes(listingId)) {
            setIsSaved(true);
        }
    }, [userModel, listingId, setIsSaved]);

    // Preload critical images (last image + first 3 images)
    useEffect(() => {
        if (images.length === 0) {
            setImagesLoaded(false);
            setLoadedImagesCount(0);
            return;
        }

        const preloadImages = () => {
            const imagesToPreload = [];
            
            // Add last image (for desktop first slide)
            if (images.length > 0) {
                imagesToPreload.push(images[images.length - 1]);
            }
            
            // Add first 3 images
            for (let i = 0; i < Math.min(3, images.length); i++) {
                if (images[i] && !imagesToPreload.includes(images[i])) {
                    imagesToPreload.push(images[i]);
                }
            }

            let loadedCount = 0;
            const totalToLoad = imagesToPreload.length;

            if (totalToLoad === 0) {
                setImagesLoaded(true);
                return;
            }

            imagesToPreload.forEach((imageUrl) => {
                const img = new Image();
                img.onload = () => {
                    loadedCount++;
                    setLoadedImagesCount(loadedCount);
                    if (loadedCount === totalToLoad) {
                        setImagesLoaded(true);
                    }
                };
                img.onerror = () => {
                    loadedCount++;
                    setLoadedImagesCount(loadedCount);
                    if (loadedCount === totalToLoad) {
                        setImagesLoaded(true);
                    }
                };
                img.src = imageUrl;
            });
        };

        preloadImages();
    }, [images]);
    
    const handleBackClick = () => {
        if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    const handleSaveClick = async () => {
        if (userModel) {
            if (isSavedLoading.current) return;
            try {
                setIsSaved((prev) => !prev);
                isSavedLoading.current = true;
                await toggleSaved(listingId, userModel, accessToken);
            } catch (err: any) {
                console.log(err);
                setIsSaved((prev) => !prev);
            } finally {
                isSavedLoading.current = false;
            }
        } else {
            setSignInMenuActivated(true);
        }
    };

    const handleShareClick = async () => {
        try {
            if (navigator.share) {
                const title = listingTitle || 'Anystay';
                const detailsParts = [
                    typeof propertyDetails.bedrooms === 'number' ? `${propertyDetails.bedrooms} bedroom${propertyDetails.bedrooms === 1 ? '' : 's'}` : undefined,
                    typeof propertyDetails.baths === 'number' ? `${propertyDetails.baths} bathroom${propertyDetails.baths === 1 ? '' : 's'}` : undefined,
                    typeof propertyDetails.beds === 'number' ? `${propertyDetails.beds} bed${propertyDetails.beds === 1 ? '' : 's'}` : undefined,
                    typeof propertyDetails.guests === 'number' ? `sleeps ${propertyDetails.guests}` : undefined,
                ].filter(Boolean).join(' · ');
                await navigator.share({
                    title,
                    text: detailsParts || listingTitle || '',
                    url: window.location.href,
                });
            } else {
                // Fallback for browsers that don't support the Web Share API
                await navigator.clipboard.writeText(window.location.href);
                alert('Link copied to clipboard!');
            }
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    const [emblaRef, emblaApi] = useEmblaCarousel();
    const [isBeginning, setIsBeginning] = useState(true);

    useEffect(() => {
        if (emblaApi) {
            emblaApi.on('select', () => {
                setIsBeginning(emblaApi.selectedScrollSnap() === 0);
            });
        }
    }, [emblaApi]);

    const scrollLeft = useCallback(() => {
        if (emblaApi) emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollRight = useCallback(() => {
        if (emblaApi) emblaApi.scrollNext();
    }, [emblaApi]);

    // Show loading skeleton while images are being preloaded
    if (!imagesLoaded || images.length === 0) {
        return (
            <CarouselPageContainer>
                <CarouselMobileBackButton onClick={handleBackClick}>
                    <IoClose style={{ height: '20px', width: '20px' }} />
                </CarouselMobileBackButton>
                <CarouselMobileShareButton onClick={handleShareClick}>
                    <img src="https://d292awxalydr86.cloudfront.net/Universal+icons/share.svg" alt="Share" style={{ height: '20px', width: '20px' }} />
                </CarouselMobileShareButton>
                <CarouselMobileSaveButton onClick={handleSaveClick}>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" style={{ height: '16px', width: '16px' }}>
                        <path
                            d="M2.26061 13.3918C5.07331 17.4376 8.89663 20.4197 12.8238 23.2792C12.9722 23.3873 13.373 23.3224 13.5518 23.1927C16.4106 21.1187 19.1902 18.9466 21.5963 16.3436C23.5642 14.2148 25.3072 11.9361 26.0237 9.04917C26.8138 5.86965 25.4788 2.5114 22.8246 0.914431C20.2484 -0.634975 16.9311 -0.189611 14.4629 2.05451L14.4198 2.09405C14.1243 2.36677 13.8509 2.66287 13.5765 2.96004C13.443 3.10465 13.3093 3.2495 13.1726 3.39204C13.0119 3.23609 12.8591 3.08852 12.7119 2.94629C12.4578 2.7008 12.2197 2.47076 11.9833 2.23899C11.949 2.20545 11.9146 2.17227 11.8799 2.13945C9.70682 0.0822306 6.68254 -0.554266 4.17227 0.529602C1.6306 1.62644 -0.0576046 4.36492 0.00150402 7.35708C0.0461959 9.62714 1.00058 11.5816 2.26061 13.3918Z"
                            fill={isSaved ? `${ColorTheme.Red.color}` : "none"}
                            stroke={isSaved ? `${ColorTheme.Red.color}` : `${ColorTheme.Black.color}`}
                            strokeWidth="2"
                            transform="translate(1, 3)"
                            style={{ boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)" }}
                        />
                    </svg>
                </CarouselMobileSaveButton>
                <CarouselWrapper>
                    <ViewListingFeaturedImage />
                </CarouselWrapper>
            </CarouselPageContainer>
        );
    }

    return (
        <CarouselPageContainer>
            <CarouselMobileBackButton onClick={handleBackClick}>
                <IoClose style={{ height: '20px', width: '20px' }} />
            </CarouselMobileBackButton>
            <CarouselMobileShareButton onClick={handleShareClick}>
                <img src="https://d292awxalydr86.cloudfront.net/Universal+icons/share.svg" alt="Share" style={{ height: '20px', width: '20px' }} />
            </CarouselMobileShareButton>
            <CarouselMobileSaveButton onClick={handleSaveClick}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" style={{ height: '16px', width: '16px' }}>
                    <path
                        d="M2.26061 13.3918C5.07331 17.4376 8.89663 20.4197 12.8238 23.2792C12.9722 23.3873 13.373 23.3224 13.5518 23.1927C16.4106 21.1187 19.1902 18.9466 21.5963 16.3436C23.5642 14.2148 25.3072 11.9361 26.0237 9.04917C26.8138 5.86965 25.4788 2.5114 22.8246 0.914431C20.2484 -0.634975 16.9311 -0.189611 14.4629 2.05451L14.4198 2.09405C14.1243 2.36677 13.8509 2.66287 13.5765 2.96004C13.443 3.10465 13.3093 3.2495 13.1726 3.39204C13.0119 3.23609 12.8591 3.08852 12.7119 2.94629C12.4578 2.7008 12.2197 2.47076 11.9833 2.23899C11.949 2.20545 11.9146 2.17227 11.8799 2.13945C9.70682 0.0822306 6.68254 -0.554266 4.17227 0.529602C1.6306 1.62644 -0.0576046 4.36492 0.00150402 7.35708C0.0461959 9.62714 1.00058 11.5816 2.26061 13.3918Z"
                        fill={isSaved ? `${ColorTheme.Red.color}` : "none"}
                        stroke={isSaved ? `${ColorTheme.Red.color}` : `${ColorTheme.Black.color}`}
                        strokeWidth="2"
                        transform="translate(1, 3)"
                        style={{ boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)" }}
                    />
                </svg>
            </CarouselMobileSaveButton>
            <CarouselWrapper>
                <CarouselViewportWithHover ref={emblaRef}>
                    <CarouselContainer>
                        {window.innerWidth >= 768 && images.length > 0 && (
                            <CarouselSlide>
                                <CarouselImage
                                    src={images[images.length - 1]}
                                    alt={`slide-${images.length - 1}`}
                                    onClick={() => {
                                        eventOnImageClick(images.length - 1);
                                    }}
                                />
                            </CarouselSlide>
                        )}
                        {images.map((url, index) => (
                            <CarouselSlide key={index}>
                                <CarouselImage
                                    src={url}
                                    alt={`slide-${index}`}
                                    onClick={() => {
                                        eventOnImageClick(index);
                                    }}
                                />
                            </CarouselSlide>
                        ))}
                    </CarouselContainer>
                    {!isBeginning && (
                        <CarouselLeftButton onClick={scrollLeft}>
                            <IoIosArrowBack style={{ height: '20px', width: 'auto' }} />
                        </CarouselLeftButton>
                    )}
                    <CarouselRightButton onClick={scrollRight}>
                        <IoIosArrowForward style={{ height: '20px', width: 'auto' }} />
                    </CarouselRightButton>
                </CarouselViewportWithHover>
                <PhotoCountIndicator>
                    <CameraIcon viewBox="0 0 20 20">
                        <rect x="3" y="6" width="14" height="10" rx="2" stroke="white" strokeWidth="1.5" fill="none"/>
                        <circle cx="10" cy="11" r="3" stroke="white" strokeWidth="1.5" fill="none"/>
                        <path d="M7 6V4.5C7 4.22386 7.22386 4 7.5 4H12.5C12.7761 4 13 4.22386 13 4.5V6" stroke="white" strokeWidth="1.5" fill="none"/>
                    </CameraIcon>
                    {images.length}
                </PhotoCountIndicator>
            </CarouselWrapper>
        </CarouselPageContainer>
    );
});

export default ViewListingCarousel;
